import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AdminShell } from '../components/layout/AdminShell';
import { catalogCopySeed, DeviceCatalogForm } from '../components/DeviceCatalogForm';
import { Modal } from '../components/Modal';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/ToastHost';
import { createCatalog, listCatalog, updateCatalog } from '../lib/adminApi';
import type { CatalogRecord, CatalogWritePayload } from '../types/registry';
import { can, useStaffMe } from '../lib/permissions';

export function DeviceCatalogPage(): JSX.Element {
  const meQuery = useStaffMe();
  const me = meQuery.data;
  const canCreate = can(me, 'device_catalog', 'create');
  const canEdit = can(me, 'device_catalog', 'edit');
  const queryClient = useQueryClient();
  const { success } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const listQuery = useQuery({ queryKey: ['catalog'], queryFn: listCatalog });
  const [createOpen, setCreateOpen] = useState(false);
  const [copySource, setCopySource] = useState<CatalogRecord | null>(null);
  const [editRow, setEditRow] = useState<CatalogRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const search = searchParams.get('q') ?? '';
  const categoryFilter = searchParams.get('category') ?? '';
  const manufacturerFilter = searchParams.get('manufacturer') ?? '';
  const allRows = listQuery.data?.items ?? [];
  const categoryOptions = listQuery.data?.categories ?? [];
  const manufacturerOptions = listQuery.data?.manufacturers ?? [];
  const modelOptions = listQuery.data?.models ?? [];
  const filteredRows = allRows.filter((row) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      row.type_name.toLowerCase().includes(q) ||
      row.type_id.toLowerCase().includes(q) ||
      (row.type_aliases ?? []).some((alias) => alias.toLowerCase().includes(q));
    const matchesCategory = !categoryFilter || row.category === categoryFilter;
    const matchesManufacturer = !manufacturerFilter || row.manufacturer === manufacturerFilter;
    return matchesSearch && matchesCategory && matchesManufacturer;
  });

  function updateFilter(key: 'q' | 'category' | 'manufacturer', value: string) {
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed) {
      next.set(key, trimmed);
    } else {
      next.delete(key);
    }
    setSearchParams(next);
  }

  function closeCreateModal() {
    setCreateOpen(false);
    setCopySource(null);
  }

  const createMutation = useMutation({
    mutationFn: (payload: CatalogWritePayload) => createCatalog(payload),
    onSuccess: async (created) => {
      closeCreateModal();
      setError(null);
      success(`Device type "${created.type_name}" created`);
      await queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ typeId, payload }: { typeId: string; payload: CatalogWritePayload }) => {
      const { type_id: _omit, ...body } = payload;
      return updateCatalog(typeId, body);
    },
    onSuccess: async (updated) => {
      setEditRow(null);
      setError(null);
      success(`Device type "${updated.type_name}" updated`);
      await queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (listQuery.isLoading) {
    return (
      <AdminShell title="Device catalog" subtitle="Device type faceplate">
        <div className="panel">
          <p className="loading-inline" aria-busy="true">
            Loading catalog…
          </p>
        </div>
      </AdminShell>
    );
  }

  const createModalOpen = createOpen || copySource !== null;
  const copySeed = copySource ? catalogCopySeed(copySource) : null;

  return (
    <AdminShell title="Device catalog" subtitle="Device type faceplate">
      <div className="panel">
        <div className="panel-toolbar">
          <p className="panel-toolbar__meta">Global device type definitions</p>
          <div className="panel-toolbar__actions">
            {canCreate ? (
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  setError(null);
                  setCopySource(null);
                  setCreateOpen(true);
                }}
              >
                Add device type
              </button>
            ) : null}
          </div>
        </div>
        {listQuery.isError ? (
          <div className="form-error">
            {listQuery.error instanceof Error ? listQuery.error.message : 'Failed to load catalog'}
          </div>
        ) : null}
        {error && !createModalOpen && !editRow ? <div className="form-error">{error}</div> : null}
        <div className="table-filters">
          <label>
            <span>Search</span>
            <input
              value={search}
              onChange={(e) => updateFilter('q', e.target.value)}
              placeholder="Search by name or ID"
            />
          </label>
          <label>
            <span>Category</span>
            <select value={categoryFilter} onChange={(e) => updateFilter('category', e.target.value)}>
              <option value="">All categories</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Manufacturer</span>
            <select
              value={manufacturerFilter}
              onChange={(e) => updateFilter('manufacturer', e.target.value)}
            >
              <option value="">All manufacturers</option>
              {manufacturerOptions.map((manufacturer) => (
                <option key={manufacturer} value={manufacturer}>
                  {manufacturer}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type ID</th>
                <th>Category</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {allRows.length === 0 && !listQuery.isLoading && !listQuery.isError ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <p className="empty-state__title">No device types yet</p>
                      <p className="empty-state__copy">
                        Add the first device type to build out the global catalog.
                      </p>
                      {canCreate ? (
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => {
                            setError(null);
                            setCopySource(null);
                            setCreateOpen(true);
                          }}
                        >
                          Add device type
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : null}
              {allRows.length > 0 && filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <p className="empty-state__title">No device types match</p>
                      <p className="empty-state__copy">
                        Try clearing the search, category, or manufacturer filters.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : null}
              {filteredRows.map((row) => (
                <tr key={row.type_id}>
                  <td>{row.type_name}</td>
                  <td>
                    <code>{row.type_id}</code>
                  </td>
                  <td>{row.category}</td>
                  <td>
                    <StatusPill status={row.status} />
                  </td>
                  <td className="table-actions">
                    {canEdit ? (
                      <button
                        type="button"
                        className="ghost-button"
                        onClick={() => {
                          setError(null);
                          setEditRow(row);
                        }}
                      >
                        Edit
                      </button>
                    ) : null}
                    {canCreate ? (
                      <button
                        type="button"
                        className="ghost-button"
                        onClick={() => {
                          setError(null);
                          setCreateOpen(false);
                          setCopySource(row);
                        }}
                      >
                        Copy
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {createModalOpen && canCreate ? (
        <Modal
          title={copySeed ? 'Copy device type' : 'Add device type'}
          onClose={closeCreateModal}
          closeOnBackdrop={false}
          xl
        >
          <p className="modal-card__copy">
            {copySeed
              ? 'Fields are cloned from the source type. Enter a new type name before creating.'
              : 'Work through the stepped sections below. All device-catalog schema fields are available; type ID is assigned automatically.'}
          </p>
          <DeviceCatalogForm
            key={copySource ? `copy-${copySource.type_id}` : 'create'}
            mode="create"
            initial={copySeed ?? undefined}
            copyFrom={Boolean(copySource)}
            categories={categoryOptions}
            manufacturers={manufacturerOptions}
            models={modelOptions}
            submitting={createMutation.isPending}
            error={error}
            onCancel={closeCreateModal}
            onSubmit={(payload) => {
              setError(null);
              createMutation.mutate(payload);
            }}
          />
        </Modal>
      ) : null}

      {editRow && canEdit ? (
        <Modal
          title="Edit device type"
          onClose={() => setEditRow(null)}
          closeOnBackdrop={false}
          xl
        >
          <p className="modal-card__copy">
            Step through each section below to review and update this device type.
          </p>
          <DeviceCatalogForm
            key={editRow.type_id}
            mode="edit"
            initial={editRow}
            categories={categoryOptions}
            manufacturers={manufacturerOptions}
            models={modelOptions}
            submitting={updateMutation.isPending}
            error={error}
            onCancel={() => setEditRow(null)}
            onSubmit={(payload) => {
              setError(null);
              updateMutation.mutate({ typeId: editRow.type_id, payload });
            }}
          />
        </Modal>
      ) : null}
    </AdminShell>
  );
}
